// One service-owned read boundary for solo and superset editors. Keep only the
// fields this client saved, never an entire PUT echo that can contain old peers.
// Confirmations retire only once overlapping older GETs have also returned.
export default class WorkoutSetReads {
  constructor() {
    this.entries = new Map();
    this.reads = 0;
    this.version = 0;
  }

  prune() {
    for (const [key, entry] of this.entries) {
      if (!entry.pending && Date.now() - entry.savedAt > 60000) this.entries.delete(key);
    }
  }

  begin() { this.prune(); this.reads += 1; return this.version; }

  remember(key, patch, kind = 'update', pending = false) {
    this.prune();
    const previous = this.entries.get(key);
    // A pending delete hides the row, but writes it drains must still advance
    // the rollback receipt in case deletion is rejected. A committed deletion
    // remains a tombstone and cannot be resurrected by a late PUT.
    if (previous?.kind === 'remove' && kind === 'update') {
      if (previous.pending) {
        previous.rollback = {
          kind: previous.rollback?.kind === 'add' ? 'add' : 'update',
          patch: { ...previous.rollback?.patch, ...patch },
          version: ++this.version, savedAt: Date.now(), confirmed: false,
        };
      }
      return;
    }
    this.entries.set(key, {
      kind: kind === 'update' && previous?.kind === 'add' ? 'add' : kind, pending, patch: { ...(kind === 'update' ? previous?.patch : {}), ...patch },
      ...(kind === 'remove' && pending ? { rollback: previous?.kind === 'remove' ? previous.rollback : previous } : {}),
      version: ++this.version, savedAt: Date.now(), confirmed: false,
    });
  }

  reconcile(serverSets, prefix, started, pending) {
    const sets = new Map(serverSets.map(set => [String(set._id), { ...set }]));
    for (const [key, entry] of this.entries) {
      if (!key.startsWith(prefix)) continue;
      const id = key.slice(prefix.length);
      const canonical = sets.get(id);
      const matches = entry.kind === 'remove' ? !canonical : canonical && Object.keys(entry.patch)
        .every(field => JSON.stringify(canonical[field] ?? null) === JSON.stringify(entry.patch[field] ?? null));
      if (!entry.pending && started >= entry.version && matches) entry.confirmed = true;
      // Bound memory-only acknowledgement protection. Durable pending edits are
      // always applied below, including after offline/background recovery.
      if (!entry.pending && Date.now() - entry.savedAt > 60000) { this.entries.delete(key); continue; }
      if (entry.kind === 'remove') sets.delete(id);
      else if (canonical || entry.kind === 'add') sets.set(id, { ...canonical, ...entry.patch });
    }
    return [...sets.values()].map(set => ({ ...set, ...pending(String(set._id)) }));
  }

  end() {
    this.reads -= 1;
    if (!this.reads) {
      for (const [key, entry] of this.entries) if (entry.confirmed) this.entries.delete(key);
    }
  }
}
